import React, { useState, useEffect } from 'react';
import { formatNumber, parseFormattedNumber, calculateItemCost } from '../../utils/helpers';

export const OrderProcessingCard = ({ order, onUpdate, onViewCustomerHistory, isExpanded, onToggleExpansion }) => {
    const [editableOrder, setEditableOrder] = useState({...order, items: order.items.map(item => {
        const hasNewFields = item.purchasePrice !== undefined;
        return {
            ...item,
            purchasePrice: hasNewFields ? (item.purchasePrice || 0) : (item.cost || 0),
            discount: hasNewFields ? String(item.discount || 0) : '0',
        };
    })});

    useEffect(() => {
        if (editableOrder.source === 'Oreka') {
            const isLegacyOrderWithFee = order.platformFee > 0;
            if (isLegacyOrderWithFee) {
                return;
            }
            const itemsTotal = editableOrder.items.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 0), 0);
            const orekaFee = Math.round(itemsTotal * 0.114);
            setEditableOrder(p => ({ ...p, platformFee: orekaFee }));
        }
    }, [editableOrder.items, editableOrder.source, order.platformFee]);


    const handleItemChange = (itemIndex, field, value) => {
        const newItems = [...editableOrder.items];
        const currentItem = { ...newItems[itemIndex] };

        if (field === 'discount') {
            currentItem[field] = value.replace(/[^0-9.]/g, '');
        } else if (['price', 'purchasePrice', 'quantity'].includes(field)) {
            currentItem[field] = parseFormattedNumber(value);
        } else {
            currentItem[field] = value;
        }

        const tempItemForCalc = {...currentItem, discount: parseFloat(currentItem.discount) || 0};
        currentItem.cost = calculateItemCost(tempItemForCalc);
        newItems[itemIndex] = currentItem;
        
        setEditableOrder(prev => ({ ...prev, items: newItems }));
    };

    const handleOrderFieldChange = (field, value) => {
        // *** FIX: Removed 'packingWarehouse' from the list of fields to be parsed as a number ***
        const numericFields = ['shippingFee', 'realShippingFee', 'promotion', 'platformFee'];
        setEditableOrder(prev => ({
            ...prev,
            [field]: numericFields.includes(field) ? parseFormattedNumber(value) : value
        }));
    };
    
    const handleUpdateClick = () => {
        const finalOrder = {
            ...editableOrder,
            items: editableOrder.items.map(item => ({
                ...item,
                discount: parseFloat(item.discount) || 0,
            }))
        };
        onUpdate(finalOrder);
    };

    const statusOptions = ["Mới", "Đang tìm hàng", "Đã đặt hàng", "Đã giao hàng", "Đã hoàn thành", "Đã hoàn", "Hủy"];
    const labelOptions = ["Không có nhãn", "Góp vốn hàng (Daklak)", "Góp vốn hàng (HCM)"];
    const warehouseOptions = ["Kho DL", "Kho HCM", "Kho khác"];

    const getSourceColor = (source) => {
        switch (source) {
            case 'Lazada': return 'text-purple-600';
            case 'Oreka': return 'text-orange-600';
            case 'Website': return 'text-red-600';
            case 'Trực tiếp': return 'text-yellow-600';
            default: return 'text-gray-600';
        }
    };

    return (
        <div id={`order-card-${order.id}`} className="bg-white rounded-lg shadow-md border border-gray-200 flex flex-col transition-all duration-300">
            {/* Header */}
            <div className="p-4 border-b bg-gray-50 rounded-t-lg cursor-pointer" onClick={onToggleExpansion}>
                <div className="flex justify-between items-start">
                    <div>
                        <button onClick={(e) => { e.stopPropagation(); onViewCustomerHistory(); }} className="font-bold text-lg text-gray-700 hover:text-blue-600 hover:underline text-left">
                            {order.customerName}
                        </button>
                        <p className={`text-sm font-bold ${getSourceColor(order.source)}`}>{order.source}</p>
                        <div className="flex items-center gap-2 mt-1">
                            <label className="text-xs text-gray-500 font-semibold">Kho:</label>
                            <select
                                name="packingWarehouse"
                                value={editableOrder.packingWarehouse || 'Kho DL'}
                                onChange={(e) => { e.stopPropagation(); handleOrderFieldChange('packingWarehouse', e.target.value); }}
                                onClick={(e) => e.stopPropagation()}
                                className="p-1 text-xs border rounded-md bg-white"
                            >
                                {warehouseOptions.map(wh => <option key={wh} value={wh}>{wh}</option>)}
                            </select>
                        </div>
                    </div>
                    <p className="text-xs font-mono text-gray-400 text-right">{order.id}</p>
                </div>
                {!isExpanded && (
                     <ul className="list-disc list-inside text-sm text-gray-600 mt-2">
                        {editableOrder.items.map((item, index) => <li key={index}>{item.productName}</li>)}
                    </ul>
                )}
            </div>

            {/* Collapsible Body */}
            {isExpanded && (
                <div className="p-4 space-y-4 flex-grow">
                    {/* Products */}
                    <div className="space-y-3">
                        {editableOrder.items.map((item, index) => (
                            <div key={index} className="p-3 border rounded-md bg-gray-50 space-y-2">
                                <p className="font-semibold text-gray-800">{item.productName}</p>
                                <div className="grid grid-cols-3 gap-2">
                                    <div>
                                        <label className="text-xs font-medium text-gray-500">Giá mua</label>
                                        <input type="text" value={formatNumber(item.purchasePrice)} onChange={e => handleItemChange(index, 'purchasePrice', e.target.value)} className="p-2 border rounded-md text-sm w-full mt-1" />
                                    </div>
                                    <div>
                                        <label className="text-xs font-medium text-gray-500">Chiết khấu</label>
                                        <div className="relative">
                                            <input type="text" value={item.discount} onChange={e => handleItemChange(index, 'discount', e.target.value)} className="p-2 pr-6 border rounded-md text-sm w-full mt-1" />
                                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 mt-0.5">%</span>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-xs font-medium text-gray-500">Giá bán</label>
                                        <input type="text" value={formatNumber(item.price)} onChange={e => handleItemChange(index, 'price', e.target.value)} className="p-2 border rounded-md text-sm w-full mt-1" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                    <div className="col-span-1">
                                        <label className="text-xs font-medium text-gray-500">Số lượng</label>
                                        <input type="text" value={item.quantity} onChange={e => handleItemChange(index, 'quantity', e.target.value)} className="p-2 border rounded-md text-sm w-full text-center mt-1" />
                                    </div>
                                    <div className="col-span-2">
                                        <label className="text-xs font-medium text-gray-500">Ghi chú</label>
                                        <input placeholder="Ghi chú cho sản phẩm..." value={item.note || ''} onChange={e => handleItemChange(index, 'note', e.target.value)} className="p-2 border rounded-md text-sm w-full mt-1" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Fees */}
                    <div className="grid grid-cols-2 gap-4 pt-3 border-t">
                         <div>
                            <label className="text-xs font-medium text-gray-500">Phí ship báo khách</label>
                            <input type="text" value={formatNumber(editableOrder.shippingFee)} onChange={e => handleOrderFieldChange('shippingFee', e.target.value)} className="p-2 border rounded-md text-sm w-full mt-1" />
                        </div>
                         <div>
                            <label className="text-xs font-medium text-gray-500">Phí ship thực trả</label>
                            <input type="text" value={formatNumber(editableOrder.realShippingFee)} onChange={e => handleOrderFieldChange('realShippingFee', e.target.value)} className="p-2 border rounded-md text-sm w-full mt-1" />
                        </div>
                         <div>
                            <label className="text-xs font-medium text-gray-500">Khuyến mãi</label>
                            <input type="text" value={formatNumber(editableOrder.promotion)} onChange={e => handleOrderFieldChange('promotion', e.target.value)} className="p-2 border rounded-md text-sm w-full mt-1" />
                        </div>
                         <div>
                            <label className="text-xs font-medium text-gray-500">Phí sàn TMĐT</label>
                            <input type="text" value={formatNumber(editableOrder.platformFee)} onChange={e => handleOrderFieldChange('platformFee', e.target.value)} className={`p-2 border rounded-md text-sm w-full mt-1 ${editableOrder.source === 'Oreka' ? 'bg-gray-100' : ''}`} disabled={editableOrder.source === 'Oreka'} />
                        </div>
                    </div>
                </div>
            )}
            
            {/* Footer (Always visible) */}
            <div className="p-4 border-t flex items-center justify-between gap-3 mt-auto">
                <select 
                    name="label"
                    value={editableOrder.label || 'Không có nhãn'} 
                    onChange={e => handleOrderFieldChange('label', e.target.value)} 
                    className="p-2 border rounded-md bg-white w-full text-sm"
                >
                    {labelOptions.map(label => <option key={label} value={label}>{label}</option>)}
                </select>
                <select 
                    name="status"
                    value={editableOrder.status} 
                    onChange={e => handleOrderFieldChange('status', e.target.value)} 
                    className="p-2 border rounded-md bg-white w-full text-sm"
                >
                    {statusOptions.map(status => <option key={status} value={status}>{status}</option>)}
                </select>
                <button onClick={handleUpdateClick} className="bg-blue-600 text-white font-bold py-2 px-5 rounded-lg hover:bg-blue-700 whitespace-nowrap">
                    Cập nhật
                </button>
            </div>
        </div>
    );
};
